/* global google */

const EXTENDED_FLAG = "__muellerExtended";
const MIN_CIRCLE_RADIUS_METERS = 10;
const MIN_RECTANGLE_METERS = 10;

const EXTENDED_ICONS = {
    circle: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2"><circle cx="12" cy="12" r="9"/></svg>',
    rectangle: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2"><rect x="4" y="6" width="16" height="12" rx="1"/></svg>',
};

const EXTENDED_LABELS = {
    circle: "Draw Circle",
    rectangle: "Draw Rectangle",
};

const SHAPE_STYLE = {
    strokeColor: "#1a73e8",
    strokeWeight: 2,
    strokeOpacity: 0.9,
    fillColor: "#1a73e8",
    fillOpacity: 0.25,
    clickable: true,
};

let installPromise = null;

function isDragMode(mode) {
    return mode === "circle" || mode === "rectangle";
}

function boundsFromLatLngPair(a, b) {
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(a);
    bounds.extend(b);
    return bounds;
}

function hasNativeCircleRectangle() {
    const overlayType = window.google?.maps?.drawing?.OverlayType;
    return Boolean(overlayType?.CIRCLE && overlayType?.RECTANGLE);
}

const SHAPE_OPTION_KEYS = [
    "circleOptions",
    "rectangleOptions",
    "polygonOptions",
    "polylineOptions",
    "markerOptions",
];

function patchSetOptions(DrawingManager) {
    const proto = DrawingManager.prototype;
    if (proto.setOptions) {
        return;
    }

    proto._removeToolbar = function () {
        if (this._toolbar && this._toolbar.parentElement) {
            this._toolbar.parentElement.removeChild(this._toolbar);
        }
        this._toolbar = null;
        this._btnElements = {};
    };

    proto.setOptions = function (options) {
        if (!options) {
            return;
        }

        const prevOptions = this._options || {};
        this._options = { ...prevOptions, ...options };

        SHAPE_OPTION_KEYS.forEach((key) => {
            if (options[key]) {
                this._options[key] = {
                    ...(prevOptions[key] || {}),
                    ...options[key],
                };
            }
        });

        if (options.drawingControlOptions) {
            this._options.drawingControlOptions = {
                ...(prevOptions.drawingControlOptions || {}),
                ...options.drawingControlOptions,
            };
        }

        if (options.map !== undefined) {
            this.setMap(options.map);
        }

        if (options.drawingControl === false) {
            this._removeToolbar();
        } else if (options.drawingControl === true && this._map) {
            this._buildToolbar();
        }

        if (options.drawingControlOptions && this._map && this._options.drawingControl !== false) {
            const prevCtrl = prevOptions.drawingControlOptions || {};
            const nextCtrl = this._options.drawingControlOptions || {};
            const modesChanged = JSON.stringify(prevCtrl.drawingModes) !== JSON.stringify(nextCtrl.drawingModes);
            const positionChanged = prevCtrl.position !== nextCtrl.position;

            if (this._toolbar && (modesChanged || positionChanged)) {
                this._removeToolbar();
                this._buildToolbar();
            }
        }

        if (options.drawingMode !== undefined) {
            this.setDrawingMode(options.drawingMode);
        }
    };
}

function extendDrawingPolyfill() {
    const drawing = window.google?.maps?.drawing;
    if (!drawing || drawing[EXTENDED_FLAG]) {
        return;
    }

    patchSetOptions(drawing.DrawingManager);

    if (hasNativeCircleRectangle()) {
        drawing[EXTENDED_FLAG] = true;
        return;
    }

    const { OverlayType, DrawingManager } = drawing;
    OverlayType.CIRCLE = "circle";
    OverlayType.RECTANGLE = "rectangle";

    const proto = DrawingManager.prototype;
    const origSetDrawingMode = proto.setDrawingMode;
    const origAttachToMap = proto._attachToMap;
    const origDetachFromMap = proto._detachFromMap;
    const origCancelCurrentDraw = proto._cancelCurrentDraw;
    const origBuildToolbar = proto._buildToolbar;
    const origHandleMapClick = proto._handleMapClick;
    const origHandleMouseMove = proto._handleMouseMove;

    proto._cancelDragDraw = function () {
        this._dragStartLatLng = null;
        this._isDragging = false;
        if (this._previewOverlay) {
            this._previewOverlay.setMap(null);
            this._previewOverlay = null;
        }
        if (this._map && this._mapDragWasEnabled !== undefined) {
            this._map.setOptions({ draggable: this._mapDragWasEnabled });
            this._mapDragWasEnabled = undefined;
        }
    };

    proto._cancelCurrentDraw = function () {
        origCancelCurrentDraw.call(this);
        this._cancelDragDraw();
    };

    proto.setDrawingMode = function (mode) {
        this._cancelDragDraw();
        origSetDrawingMode.call(this, mode);
    };

    proto._handleDragMouseDown = function (e) {
        if (!this._currentMode || !isDragMode(this._currentMode) || !e.latLng) {
            return;
        }

        this._cancelDragDraw();
        this._dragStartLatLng = e.latLng;
        this._isDragging = true;
        this._mapDragWasEnabled = this._map.get("draggable") !== false;
        this._map.setOptions({ draggable: false });

        if (this._currentMode === OverlayType.CIRCLE) {
            this._previewOverlay = new google.maps.Circle({
                map: this._map,
                center: e.latLng,
                radius: 0,
                ...SHAPE_STYLE,
                ...(this._options.circleOptions || {}),
            });
        } else {
            this._previewOverlay = new google.maps.Rectangle({
                map: this._map,
                bounds: boundsFromLatLngPair(e.latLng, e.latLng),
                ...SHAPE_STYLE,
                ...(this._options.rectangleOptions || {}),
            });
        }
    };

    proto._handleDragMouseMove = function (e) {
        if (!this._isDragging || !this._dragStartLatLng || !this._previewOverlay || !e.latLng) {
            return;
        }

        if (this._currentMode === OverlayType.CIRCLE) {
            const radius = google.maps.geometry.spherical.computeDistanceBetween(
                this._dragStartLatLng,
                e.latLng
            );
            this._previewOverlay.setRadius(radius);
        } else {
            this._previewOverlay.setBounds(
                boundsFromLatLngPair(this._dragStartLatLng, e.latLng)
            );
        }
    };

    proto._handleDragMouseUp = function () {
        if (!this._isDragging || !this._dragStartLatLng || !this._previewOverlay) {
            return;
        }

        const mode = this._currentMode;
        const overlay = this._previewOverlay;
        this._previewOverlay = null;
        this._isDragging = false;
        this._dragStartLatLng = null;

        if (this._map && this._mapDragWasEnabled !== undefined) {
            this._map.setOptions({ draggable: this._mapDragWasEnabled });
            this._mapDragWasEnabled = undefined;
        }

        let isValid = false;
        if (mode === OverlayType.CIRCLE) {
            isValid = overlay.getRadius() >= MIN_CIRCLE_RADIUS_METERS;
        } else if (mode === OverlayType.RECTANGLE) {
            const ne = overlay.getBounds().getNorthEast();
            const sw = overlay.getBounds().getSouthWest();
            const width = google.maps.geometry.spherical.computeDistanceBetween(
                new google.maps.LatLng(sw.lat(), sw.lng()),
                new google.maps.LatLng(sw.lat(), ne.lng())
            );
            const height = google.maps.geometry.spherical.computeDistanceBetween(
                new google.maps.LatLng(sw.lat(), sw.lng()),
                new google.maps.LatLng(ne.lat(), sw.lng())
            );
            isValid = width >= MIN_RECTANGLE_METERS && height >= MIN_RECTANGLE_METERS;
        }

        if (!isValid) {
            overlay.setMap(null);
            return;
        }

        const self = this;
        google.maps.event.trigger(self, "overlaycomplete", {
            type: mode,
            overlay,
        });

        if (mode === OverlayType.CIRCLE) {
            google.maps.event.trigger(self, "circlecomplete", overlay);
        } else if (mode === OverlayType.RECTANGLE) {
            google.maps.event.trigger(self, "rectanglecomplete", overlay);
        }

        this.setDrawingMode(null);
    };

    proto._handleMapClick = function (e) {
        if (isDragMode(this._currentMode)) {
            return;
        }
        origHandleMapClick.call(this, e);
    };

    proto._handleMouseMove = function (e) {
        if (this._isDragging) {
            this._handleDragMouseMove(e);
            return;
        }
        origHandleMouseMove.call(this, e);
    };

    proto._attachToMap = function () {
        origAttachToMap.call(this);

        const self = this;
        this._dragListeners = [
            google.maps.event.addListener(this._map, "mousedown", (event) => {
                self._handleDragMouseDown(event);
            }),
            google.maps.event.addDomListener(document, "mouseup", () => {
                self._handleDragMouseUp();
            }),
        ];
    };

    proto._detachFromMap = function () {
        if (this._dragListeners) {
            this._dragListeners.forEach((listener) => {
                google.maps.event.removeListener(listener);
            });
            this._dragListeners = [];
        }
        this._cancelDragDraw();
        origDetachFromMap.call(this);
    };

    proto._buildToolbar = function () {
        origBuildToolbar.call(this);

        const drawModes = (this._options.drawingControlOptions || {}).drawingModes || [];
        drawModes.forEach((mode) => {
            const button = this._btnElements[mode];
            if (!button) {
                return;
            }
            if (EXTENDED_ICONS[mode]) {
                button.innerHTML = EXTENDED_ICONS[mode];
            }
            if (EXTENDED_LABELS[mode]) {
                button.title = EXTENDED_LABELS[mode];
            }
        });
    };

    drawing[EXTENDED_FLAG] = true;
}

function loadBasePolyfill() {
    if (window.google?.maps?.drawing) {
        return Promise.resolve();
    }

    return import("./mcx-drawing-polyfill.js");
}

/**
 * Loads the mcx-drawing-polyfill after the Google Maps API is ready and extends
 * DrawingManager with circle and rectangle support required by maps.js.
 */
export function installDrawingPolyfill() {
    if (!window.google?.maps) {
        return Promise.reject(new Error("Google Maps API must load before installDrawingPolyfill()"));
    }

    if (window.google.maps.drawing?.[EXTENDED_FLAG]) {
        return Promise.resolve();
    }

    if (!installPromise) {
        installPromise = loadBasePolyfill()
            .then(() => {
                extendDrawingPolyfill();
            })
            .catch((error) => {
                installPromise = null;
                throw error;
            });
    }

    return installPromise;
}
